// TESSERAX — RÉSONANCES DU JOUR (mutateurs du défi quotidien).
//
// Chaque défi du jour porte une « résonance » : un mutateur déterministe choisi à
// partir de la graine du jour. Tous les joueurs d'une même journée affrontent la
// MÊME résonance (social + rejouabilité) et, comme la séquence de pièces, elle se
// rejoue identiquement partout (pur, dérivé de la graine — pas de Math.random).
//
// Module PUR : ni THREE ni DOM. main.ts lit `pickDailyMutator(seed)` et applique
// les buffs renvoyés sur ses variables de run existantes (mêmes leviers que les
// reliques : runScoreMult, runSpeedMult, maxCharges…). La résonance « neutre »
// laisse une partie quotidienne vanilla pour ne pas dénaturer le mode chaque jour.
import { mulberry32 } from './rng';

// Buffs appliqués à une run. `speedMult` > 1 = chute PLUS LENTE (cohérent avec
// runSpeedMult dans main.ts) ; `lockMult` < 1 = verrouillage plus rapide (plus dur).
export interface MutatorBuffs {
  scoreMult: number;
  speedMult: number;
  meterMult: number;
  comboMult: number;
  maxChargesAdd: number;   // +charges maximum de Bascule
  startCharges: number;    // charges offertes au départ
  lockMult: number;        // multiplie le délai de verrouillage
  freeHold: boolean;       // réserve illimitée
  riseRateMult: number;    // STRUCTUREL : >1 = montée d'instabilité plus FRÉQUENTE.
                           // GELÉ : la montée est désactivée en jeu (cf. core/modes.rise=false) →
                           // ce levier n'a plus aucun effet et AUCUN mutateur ne le règle (reste = 1).
}

export interface Mutator {
  id: string;
  name: string;            // FR (défaut ; EN via i18n)
  text: string;            // FR, une ligne lisible
  tag: 'neutre' | 'bonus' | 'risque';
  apply: (b: MutatorBuffs) => void;
}

export function freshBuffs(): MutatorBuffs {
  return { scoreMult: 1, speedMult: 1, meterMult: 1, comboMult: 1, maxChargesAdd: 0, startCharges: 0, lockMult: 1, freeHold: false, riseRateMult: 1 };
}

// Catalogue data-driven (croît par ajout d'entrées, comme reliques/succès).
export const MUTATORS: Mutator[] = [
  { id: 'neutre', name: 'Chambre neutre', tag: 'neutre',
    text: 'Aucune variante — le défi à l’état pur.',
    apply: () => {} },
  { id: 'gravite', name: 'Gravité faible', tag: 'bonus',
    text: 'Les pièces tombent 30 % plus lentement, mais les points sont réduits de 10 %.',
    apply: b => { b.speedMult *= 1.3; b.scoreMult *= 0.9; } },
  { id: 'densite', name: 'Densité', tag: 'risque',
    text: 'Chute 18 % plus rapide, mais +35 % à tous les points.',
    apply: b => { b.speedMult *= 0.82; b.scoreMult *= 1.35; } },
  { id: 'surcharge', name: 'Surcharge', tag: 'risque',
    text: 'Score ×1.6, mais le délai de verrouillage est raccourci de 30 %.',
    apply: b => { b.scoreMult *= 1.6; b.lockMult *= 0.7; } },
  { id: 'tectonique', name: 'Tectonique libre', tag: 'bonus',
    text: '+2 charges de Bascule maximum et 1 charge offerte au départ.',
    apply: b => { b.maxChargesAdd += 2; b.startCharges += 1; } },
  { id: 'flux', name: 'Flux de Zone', tag: 'bonus',
    text: 'La jauge de Zone se remplit 60 % plus vite.',
    apply: b => { b.meterMult *= 1.6; } },
  { id: 'main_legere', name: 'Main légère', tag: 'bonus',
    text: 'Réserve illimitée et bonus d’enchaînement renforcé (×1.25).',
    apply: b => { b.freeHold = true; b.comboMult *= 1.25; } },
  { id: 'resonance', name: 'Résonance pure', tag: 'bonus',
    text: 'Bonus d’enchaînement très renforcé (×1.8).',
    apply: b => { b.comboMult *= 1.8; } },
  { id: 'tempete', name: 'Tempête', tag: 'risque',
    text: 'Chute 20 % plus rapide et verrouillage raccourci de 15 %, mais score ×1.5.',
    apply: b => { b.speedMult *= 0.8; b.lockMult *= 0.85; b.scoreMult *= 1.5; } },
  { id: 'abondance', name: 'Abondance', tag: 'bonus',
    text: 'Réserve illimitée, +1 charge de Bascule maximum et 2 charges offertes au départ.',
    apply: b => { b.freeHold = true; b.maxChargesAdd += 1; b.startCharges += 2; } },
  // « Tellurique » : risque/récompense via des leviers VIVANTS (chute + verrouillage).
  // À l'origine son malus passait par riseRateMult (montée plus fréquente) ; or la montée
  // d'instabilité est GELÉE/désactivée (cf. core/modes.rise=false), si bien que ce malus
  // était INERTE → +50 % de score SANS contrepartie (un jour de défi sur ~11 offrait un bonus
  // gratuit, faussant le classement quotidien ET éternel). On rebâtit le malus sur la chute et
  // le verrouillage, eux bien appliqués par main.ts (interval / lockDelayMult) → vrai « risque ».
  { id: 'tellurique', name: 'Tellurique', tag: 'risque',
    text: 'Chute 18 % plus rapide et verrouillage raccourci de 18 %, mais +50 % à tous les points.',
    apply: b => { b.speedMult *= 0.82; b.lockMult *= 0.82; b.scoreMult *= 1.5; } },
];

export function mutatorById(id: string): Mutator | undefined {
  return MUTATORS.find(m => m.id === id);
}

// Choisit la résonance du jour : pure fonction de la graine (déterministe et
// reproductible partout). Dérive un flux distinct de celui des pièces (XOR d'une
// constante de Weyl) → ne consomme PAS la RNG de gameplay, séquence inchangée.
export function pickDailyMutator(seed: number): Mutator {
  if (!MUTATORS.length) throw new Error('MUTATORS catalogue vide');   // garde-fou : ne renvoie jamais undefined
  const s = (seed ^ 0x9e3779b9) >>> 0;
  const idx = Math.min(MUTATORS.length - 1, Math.floor(mulberry32(s)() * MUTATORS.length));   // clamp (mulberry32 ∈ [0,1) → idx < length)
  return MUTATORS[idx];
}

// Construit les buffs d'une résonance (helper testable, sans état).
export function buffsFor(m: Mutator): MutatorBuffs {
  const b = freshBuffs();
  m.apply(b);
  return b;
}
